import { type ComponentProps, type ReactNode, Suspense, useEffect, useRef } from 'react';
import { type Selection, Text } from 'react-aria-components';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { ensureCreationDateMarker } from '@gonfalon/flag-filters/ensureCreationDateMarker';
import type { CreationDate } from '@gonfalon/flag-filters/types';
import { MaintainerDialog } from '@gonfalon/maintainer/MaintainerDialog';
import { MemberMaintainerTag } from '@gonfalon/members/MemberMaintainerTag';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useSuspenseTeam } from '@gonfalon/rest-api/hooks/useSuspenseTeam';
import { TextButton } from '@gonfalon/ui/Button';
import { DialogHeading } from '@gonfalon/ui/Dialog';
import { Picker } from '@gonfalon/ui/Picker';
import { Stack } from '@gonfalon/ui/stack';
import { Tag, TagFallback, TagGroup, TagIcon } from '@gonfalon/ui/TagGroup';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { Box } from '@launchpad-ui/box';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  DialogTrigger,
  ListBoxItem,
  Popover,
  ProgressBar,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import { EntityTagFilter } from '#components/EntityTagFilter/EntityTagFilter';

import type { AiConfigModeOption } from '../constants';
import { useAvailableModes } from '../hooks/useAvailableModes';
import { shouldShowAiConfigModeTabs } from '../utils/shouldShowAiConfigModeTabs';

import type { AiConfigFilter } from './helpers';

import styles from './AiConfigFiltersDialog.module.css';

type Props = {
  filters: AiConfigFilter | undefined;
  onClear: (() => void) | undefined;
  onChange: (filters: AiConfigFilter) => void;
};

export function AiConfigFiltersDialog({ filters = {}, onChange, onClear }: Props) {
  const tags = filters.tags ?? [];
  const modes = filters.mode ?? [];

  const maintainerId = filters.maintainerId;
  const maintainerTeamKey = filters.maintainerTeamKey;

  const formRef = useRef<HTMLFormElement | null>(null);

  const isModeTabsEnabled = shouldShowAiConfigModeTabs();
  const allModeOptions = useAvailableModes();
  const availableModeOptions = allModeOptions.filter((option) => option.id !== 'all');

  const shouldShowModeFilter = !isModeTabsEnabled && availableModeOptions.length > 1;

  useEffect(() => {
    const validModes = modes.filter((mode) => availableModeOptions.some((option) => option.id === mode));
    if (validModes.length !== modes.length) {
      onChange({ ...filters, mode: validModes.length > 0 ? validModes : undefined });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [modes, availableModeOptions]);

  return (
    <Dialog className={styles.filters}>
      {({ close }) => (
        <>
          <header className={styles.header}>
            <DialogHeading onClose={close}>
              <Message id="aiConfigs.filtersDialog.heading" />
            </DialogHeading>
          </header>

          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary
                owner="team-ai-agents"
                onReset={reset}
                severity="medium"
                fallbackRender={({ error, resetErrorBoundary }) => {
                  let action: ReactNode | undefined;
                  if (isRESTAPIError(error) && error.status !== 403) {
                    action = (
                      <Button size="small" onPress={() => resetErrorBoundary()}>
                        {msg('aiConfigs.filtersDialog.tryAgain')}
                      </Button>
                    );
                  }

                  return (
                    <div>
                      <Alert variant="inline" status="error">
                        <Message id="aiConfigs.filtersDialog.loadError" />{' '}
                        {action !== undefined ? (
                          <ButtonGroup>{action}</ButtonGroup>
                        ) : (
                          msg('aiConfigs.filtersDialog.loadErrorNotified')
                        )}
                      </Alert>
                    </div>
                  );
                }}
              >
                <div className={styles.controls}>
                  <form ref={formRef} className={styles.form}>
                    <div className={styles.project}>
                      <Stack gap="4">
                        {shouldShowModeFilter && (
                          <AiConfigModeTagGroup
                            value={modes}
                            options={allModeOptions}
                            onChange={(nextModes) =>
                              onChange({ ...filters, mode: nextModes.length > 0 ? nextModes : undefined })
                            }
                          />
                        )}

                        {/* Tag filter */}
                        <EntityTagFilter
                          tags={tags}
                          kind="aiconfig"
                          onChange={(nextFilters) => onChange({ ...filters, tags: nextFilters.tags })}
                        />

                        {/* Maintainer filter */}
                        <TagGroup
                          label={msg('aiConfigs.filtersDialog.maintainerLabel')}
                          onRemove={() =>
                            onChange({
                              ...filters,
                              maintainerId: undefined,
                              maintainerTeamKey: undefined,
                            })
                          }
                          items={
                            maintainerId !== undefined
                              ? [{ id: maintainerId, type: 'member' as const }]
                              : maintainerTeamKey !== undefined
                                ? [{ id: maintainerTeamKey, type: 'team' as const }]
                                : []
                          }
                          renderEmptyState={() => 'Anyone'}
                          renderAddTrigger={() => (
                            <DialogTrigger>
                              <TextButton size="small">{msg('aiConfigs.filtersDialog.chooseMaintainer')}</TextButton>
                              <Popover>
                                <Suspense
                                  fallback={
                                    <Box
                                      width="$320"
                                      height="$208"
                                      display="grid"
                                      alignItems="center"
                                      justifyContent="center"
                                    >
                                      <ProgressBar
                                        aria-label={msg('aiConfigs.filtersDialog.maintainersLoadingLabel')}
                                        isIndeterminate
                                        size="small"
                                      />
                                    </Box>
                                  }
                                >
                                  <MaintainerDialog
                                    selectionMode="single"
                                    closeOnSelection
                                    maintainerId={maintainerId}
                                    maintainerTeamKey={maintainerTeamKey}
                                    onMemberSelectionChange={(key) =>
                                      onChange({
                                        ...filters,
                                        maintainerTeamKey: undefined,
                                        maintainerId: key !== undefined ? String(key) : undefined,
                                      })
                                    }
                                    onTeamSelectionChange={(key) =>
                                      onChange({
                                        ...filters,
                                        maintainerId: undefined,
                                        maintainerTeamKey: key !== undefined ? String(key) : undefined,
                                      })
                                    }
                                  />
                                </Suspense>
                              </Popover>
                            </DialogTrigger>
                          )}
                        >
                          {({ id, type }) => (
                            <Suspense
                              fallback={
                                <TagFallback>
                                  <TagFallback.Visual />
                                  <TagFallback.Text />
                                </TagFallback>
                              }
                            >
                              {type === 'member' ? (
                                <MemberMaintainerTag maintainerId={id} />
                              ) : type === 'team' ? (
                                <TeamMaintainerTag maintainerTeamKey={id} />
                              ) : null}
                            </Suspense>
                          )}
                        </TagGroup>

                        {/* Creation date filter */}
                        <CreationDatePicker
                          value={filters.created}
                          onSelectionChange={(value) =>
                            onChange({
                              ...filters,
                              created: value !== '' ? ensureCreationDateMarker(value) : undefined,
                            })
                          }
                        />
                      </Stack>
                    </div>
                  </form>
                </div>

                {onClear && (
                  <div className={styles.footer}>
                    <Button
                      size="small"
                      onPress={() => {
                        formRef.current?.reset();
                        onClear();
                      }}
                    >
                      {msg('aiConfigs.filtersDialog.clear')}
                    </Button>
                  </div>
                )}
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </>
      )}
    </Dialog>
  );
}

function TeamMaintainerTag({ maintainerTeamKey }: { maintainerTeamKey: string }) {
  const { data: team } = useSuspenseTeam({ teamKey: maintainerTeamKey });

  return (
    <Tag aria-label={team.name} id={maintainerTeamKey} textValue={maintainerTeamKey}>
      <TagIcon>
        <Icon name="group" size="small" />
      </TagIcon>{' '}
      <TextTruncator>{team.name}</TextTruncator>
    </Tag>
  );
}

export type AiConfigModeTagGroupProps = {
  value: readonly string[];
  options: readonly AiConfigModeOption[];
  onChange: (modes: string[]) => void;
};

export function AiConfigModeTagGroup({ value, options, onChange }: AiConfigModeTagGroupProps) {
  const modeIds = options.filter((option) => option.id !== 'all').map((option) => option.id);
  const selectedModes = value.filter((mode) => modeIds.some((id) => id === mode));
  const selectedKeys = new Set<string>(selectedModes.length > 0 ? selectedModes : ['all']);

  return (
    <TagGroup
      label={msg('aiConfigs.filtersDialog.modeLabel')}
      selectionMode="multiple"
      selectedKeys={selectedKeys}
      onSelectionChange={(keys: Selection) => {
        if (keys === 'all') {
          onChange([]);
          return;
        }
        const nextKeys = Array.from(keys).map(String);
        if (nextKeys.includes('all') && !selectedKeys.has('all')) {
          onChange([]);
          return;
        }
        onChange(nextKeys.filter((key) => key !== 'all'));
      }}
    >
      {options.map((option) => (
        <Tag key={option.id} id={option.id}>
          {option.label}
        </Tag>
      ))}
    </TagGroup>
  );
}

function CreationDatePicker({
  value,
  onSelectionChange,
}: {
  value: CreationDate | undefined;
  onSelectionChange: ComponentProps<typeof Picker>['onSelectionChange'];
}) {
  return (
    <Picker
      label={msg('aiConfigs.filtersDialog.createdLabel')}
      defaultSelectedKey={value ?? ''}
      onSelectionChange={onSelectionChange}
    >
      <ListBoxItem id="" textValue="any">
        <Text slot="label">
          <Message id="aiConfigs.filtersDialog.createdAnytime" />
        </Text>
      </ListBoxItem>
      <ListBoxItem id="1-week-ago" textValue={msg('aiConfigs.filtersDialog.createdOverOneWeekTextValue')}>
        <Text slot="label">
          <Message id="aiConfigs.filtersDialog.createdOverOneWeek" />
        </Text>
      </ListBoxItem>
      <ListBoxItem id="1-month-ago" textValue={msg('aiConfigs.filtersDialog.createdOverOneMonthTextValue')}>
        <Text slot="label">
          <Message id="aiConfigs.filtersDialog.createdOverOneMonth" />
        </Text>
      </ListBoxItem>
      <ListBoxItem id="2-months-ago" textValue={msg('aiConfigs.filtersDialog.createdOverTwoMonthsTextValue')}>
        <Text slot="label">
          <Message id="aiConfigs.filtersDialog.createdOverTwoMonths" />
        </Text>
      </ListBoxItem>
      <ListBoxItem id="3-months-ago" textValue={msg('aiConfigs.filtersDialog.createdOverThreeMonthsTextValue')}>
        <Text slot="label">
          <Message id="aiConfigs.filtersDialog.createdOverThreeMonths" />
        </Text>
      </ListBoxItem>
      <ListBoxItem id="6-months-ago" textValue={msg('aiConfigs.filtersDialog.createdOverSixMonthsTextValue')}>
        <Text slot="label">
          <Message id="aiConfigs.filtersDialog.createdOverSixMonths" />
        </Text>
      </ListBoxItem>
      <ListBoxItem id="1-year-ago" textValue={msg('aiConfigs.filtersDialog.createdOverOneYearTextValue')}>
        <Text slot="label">
          <Message id="aiConfigs.filtersDialog.createdOverOneYear" />
        </Text>
      </ListBoxItem>
    </Picker>
  );
}
